import { snakeize, camelize } from 'casing'
import { Meta } from '@/types'

interface JsonResponse<T> {
  data: T;
  meta: Meta;
}

class APIError extends Error {
  json: Record<string, unknown>

  constructor (json: Record<string, unknown>) {
    super()

    this.json = json
  }
}

export class APIClient {
  static get<T> (path: string) {
    return APIClient.http<T>(path, { method: 'GET' })
  }

  static post<T> (path: string, params: Record<string, unknown>) {
    return APIClient.http<T>(path, { method: 'POST', body: JSON.stringify(snakeize(params)) })
  }

  static postFormData<T> (path: string, body: FormData) {
    return APIClient.http<T>(path, { method: 'POST', body })
  }

  static put<T> (path: string, params: Record<string, unknown>) {
    return APIClient.http<T>(path, { method: 'PUT', body: JSON.stringify(snakeize(params)) })
  }

  static delete (path: string) {
    return APIClient.http(path, { method: 'DELETE' })
  }

  static http<T> (path: string, request: { method: string, body?: FormData | string }): Promise<JsonResponse<T>> {
    const fullpath = `/squeegee/api/${path}`.replace('//', '/')
    const headers = new Headers({
      'Content-Type': 'application/json; charset=utf-8',
      'X-CSRF-Token': APIClient.csrfToken()
    })

    if (request.body instanceof FormData) {
      headers.delete('Content-Type')
    }

    return fetch(fullpath, { ...request, headers, credentials: 'include' }).catch(() => {
      throw new APIError({ errors: ['ネットワークエラーが発生しました'] })
    }).then((res: Response) => {
      if (res.status === 204) {
        return { data: {}, meta: {} }
      } else {
        return res.json().then(json => {
          if (res.ok) {
            return camelize(json)
          } else {
            throw new APIError(camelize(json))
          }
        })
      }
    })
  }

  static csrfToken (): string {
    const meta = document.querySelector('meta[name=csrf-token]') as HTMLMetaElement

    return meta.content
  }
}
